/**
 * MapToolbar — barra vertical flotante con controles del visor SIG.
 *
 * Iconos diferenciados (vs. visor heredado huimilpan_elawan donde fit-aoi y
 * fullscreen usaban el mismo símbolo de "corners"):
 *   - **Centrar en AOI** -> icono crosshair / target
 *   - **Mapa completo**  -> icono corners (frame)
 *
 * Paneles desplegables (drawer izquierdo de la toolbar):
 *   - **Capas**:    toggle ON/OFF de cada capa visible
 *   - **Ajustes**:  basemap (claro/oscuro/satélite), opacidad parcelas
 *
 * Fullscreen es **controlable** desde el caller (props `fullscreenActive` +
 * `onToggleFullscreen`) para que la app maneje el modo "fullmap" interno
 * (esconder panel lateral) en vez del fullscreen del browser.
 */
import { useCallback, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import type { MapRef } from 'react-map-gl';
import { fitMapToAreaOfInterest } from './fitMapToAoi';

export type BasemapId = 'dark' | 'light' | 'satellite' | 'streets';

export interface BasemapOption {
  id: BasemapId;
  label: string;
  styleUrl: string;
}

/** Satélite + etiquetas. Default del visor; no depende del tema claro/oscuro de la UI. */
export const MAPBOX_SATELLITE_STREETS = 'mapbox://styles/mapbox/satellite-streets-v12';

export const BASEMAP_OPTIONS: BasemapOption[] = [
  { id: 'dark',      label: 'Oscuro',    styleUrl: 'mapbox://styles/mapbox/dark-v11' },
  { id: 'light',     label: 'Claro',     styleUrl: 'mapbox://styles/mapbox/light-v11' },
  { id: 'satellite', label: 'Satélite',  styleUrl: MAPBOX_SATELLITE_STREETS },
  { id: 'streets',   label: 'Calles',    styleUrl: 'mapbox://styles/mapbox/streets-v12' },
];

export interface LayerToggle {
  /** Clave de la capa: `keyof CapasConfig` para las estáticas, o `capa:<uuid>`
   *  para capas informativas dinámicas (canal abierto). */
  key: string;
  label: string;
  /** Si la capa esta activa. */
  enabled: boolean;
}

interface MapToolbarProps {
  mapRef: React.RefObject<MapRef>;
  /** Capas actuales para mostrar el toggle. */
  layers?: LayerToggle[];
  /** Callback al togglear una capa desde el panel "Capas". */
  onLayerToggle?: (key: string, next: boolean) => void;
  /** Basemap actual (id). */
  currentBasemap?: BasemapId;
  /** Callback al cambiar basemap desde "Ajustes". */
  onBasemapChange?: (id: BasemapId, styleUrl: string) => void;
  /** Estado del modo "mapa completo" (controlado por la app). */
  fullscreenActive?: boolean;
  /** Si el caller maneja el toggle de fullscreen (recomendado). */
  onToggleFullscreen?: () => void;
  /** Filtros overlay del mapa: visible u oculto (solo si se pasa `onToggleFilterBar`). */
  filterBarVisible?: boolean;
  onToggleFilterBar?: () => void;
  /** Posicion lateral. Default: 'right'. */
  position?: 'right' | 'left';
  /** Distancia desde el top del map shell en px. Default: 14 (pegado al
   *  topbar superior). Antes era 70 — dejaba demasiado espacio vacío
   *  arriba y los botones zoom-in/out se salían del canvas en mapas
   *  cortos. Bajar a 14 da el máximo de espacio vertical disponible
   *  para todos los botones sin necesidad de compactarlos. Subir si
   *  hay un FilterBar overlay encima del mapa. */
  topOffset?: number;
}

export function MapToolbar({
  mapRef,
  layers,
  onLayerToggle,
  currentBasemap = 'dark',
  onBasemapChange,
  position = 'right',
  topOffset = 14,
}: MapToolbarProps) {
  const [activePanel, setActivePanel] = useState<'layers' | 'settings' | null>(null);
  const [pitchActive, setPitchActive] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap()?.zoomIn({ duration: 250 });
  }, [mapRef]);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap()?.zoomOut({ duration: 250 });
  }, [mapRef]);

  const handleFitAoi = useCallback(() => {
    const mb = mapRef.current?.getMap();
    if (mb) fitMapToAreaOfInterest(mb);
  }, [mapRef]);

  const handleTogglePitch = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const current = map.getPitch();
    const target = current > 5 ? 0 : 50;
    map.easeTo({ pitch: target, duration: 600 });
    setPitchActive(target > 5);
  }, [mapRef]);

  const togglePanel = (which: 'layers' | 'settings') => {
    setActivePanel((cur) => (cur === which ? null : which));
  };

  // CSS variables para posicionar.
  // bottom: 14 fija el límite inferior del shell — sin esto el toolbar
  // (display flex column con N botones) se extendía indefinidamente y
  // en mapas cortos quedaba fuera del canvas visible. Con top+bottom
  // ambos a 14, el shell siempre ocupa "alto del mapa - 28px" y el
  // .map-toolbar dentro usa max-height:100% + overflow para scrollear
  // si los botones no caben.
  const wrapperStyle: CSSProperties = {
    position: 'absolute',
    top: topOffset,
    bottom: 14,
    [position]: 14,
    zIndex: 55,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    flexDirection: position === 'right' ? 'row-reverse' : 'row',
    pointerEvents: 'none', // los hijos lo tienen activado
  };

  return (
    <div ref={wrapRef} className="map-toolbar-shell" style={wrapperStyle}>
      <div className="map-toolbar glass-card" role="toolbar" aria-label="Herramientas del mapa">
        <ToolButton
          title="Capas"
          active={activePanel === 'layers'}
          onClick={() => togglePanel('layers')}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12,2 22,8.5 12,15 2,8.5" />
              <polyline points="2,15.5 12,22 22,15.5" />
            </svg>
          }
        />
        <ToolButton
          title="Ajustes"
          active={activePanel === 'settings'}
          onClick={() => togglePanel('settings')}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.07a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          }
        />
        {/* Centrar en AOI: icono CROSSHAIR / TARGET (distinto del fullscreen). */}
        <ToolButton
          title="Centrar en área de interés"
          onClick={handleFitAoi}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="3" />
              <line x1="12" y1="2" x2="12" y2="5" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="2" y1="12" x2="5" y2="12" />
              <line x1="19" y1="12" x2="22" y2="12" />
            </svg>
          }
        />
        <ToolButton
          title="Alternar vista 2D / 3D"
          active={pitchActive}
          onClick={handleTogglePitch}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 4.5 L9.4 13 L12 11 L14.6 13 Z" fill="currentColor" stroke="none" />
              <path d="M12 11 L9.4 13 L12 19.5 L14.6 13 Z" opacity="0.45" fill="currentColor" stroke="none" />
            </svg>
          }
        />
        <div className="map-toolbar-divider" />
        <ToolButton
          title="Acercar"
          onClick={handleZoomIn}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
        />
        <ToolButton
          title="Alejar"
          onClick={handleZoomOut}
          icon={
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
        />
      </div>

      {activePanel === 'layers' && (
        <LayersPanel
          layers={layers ?? []}
          onToggle={onLayerToggle}
          onClose={() => setActivePanel(null)}
        />
      )}
      {activePanel === 'settings' && (
        <SettingsPanel
          currentBasemap={currentBasemap}
          onBasemapChange={onBasemapChange}
          onClose={() => setActivePanel(null)}
        />
      )}
    </div>
  );
}

interface ToolButtonProps {
  title: string;
  active?: boolean;
  onClick: () => void;
  icon: ReactNode;
}

function ToolButton({ title, active, onClick, icon }: ToolButtonProps) {
  return (
    <button
      type="button"
      className={`map-tool-btn${active ? ' is-active' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
      style={{ pointerEvents: 'auto' }}
    >
      {icon}
    </button>
  );
}

/* ──────────────────────────── Layers panel ──────────────────────────── */
function LayersPanel({
  layers,
  onToggle,
  onClose,
}: {
  layers: LayerToggle[];
  onToggle?: (key: string, next: boolean) => void;
  onClose: () => void;
}) {
  return (
    <div className="map-toolbar-panel glass-card" role="dialog" aria-label="Capas">
      <header className="map-toolbar-panel-head">
        <span className="map-toolbar-panel-title">Capas</span>
        <button type="button" className="map-toolbar-panel-close" onClick={onClose} aria-label="Cerrar">×</button>
      </header>
      <div className="map-toolbar-panel-body">
        {layers.length === 0 ? (
          <p className="map-toolbar-panel-empty">No hay capas configuradas.</p>
        ) : (
          <ul className="map-toolbar-layer-list">
            {layers.map((l) => (
              <li key={String(l.key)}>
                <label className="map-toolbar-layer-row">
                  <input
                    type="checkbox"
                    checked={l.enabled}
                    onChange={(e) => onToggle?.(l.key, e.target.checked)}
                  />
                  <span>{l.label}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────── Settings panel ──────────────────────────── */
function SettingsPanel({
  currentBasemap,
  onBasemapChange,
  onClose,
}: {
  currentBasemap: BasemapId;
  onBasemapChange?: (id: BasemapId, styleUrl: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="map-toolbar-panel glass-card" role="dialog" aria-label="Ajustes">
      <header className="map-toolbar-panel-head">
        <span className="map-toolbar-panel-title">Ajustes</span>
        <button type="button" className="map-toolbar-panel-close" onClick={onClose} aria-label="Cerrar">×</button>
      </header>
      <div className="map-toolbar-panel-body">
        <div className="map-toolbar-block">
          <span className="map-toolbar-block-label">Mapa base</span>
          <div className="map-toolbar-basemap-grid">
            {BASEMAP_OPTIONS.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`map-toolbar-basemap-btn${currentBasemap === b.id ? ' is-active' : ''}`}
                onClick={() => onBasemapChange?.(b.id, b.styleUrl)}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
